import { useState } from "react";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { AirSavedCardModel, isAirSavedCardModel } from "entities/AirSavedCard";
import { SeaSavedCardModel } from "entities/SeaSavedCard";
import { createAirUpdateCardHook } from "./useAirCardUpdateAction";

type UseAirUpdateAllCardsActionParams = {
  filteredCards: (SeaSavedCardModel | AirSavedCardModel)[];
  airSavedCards: AirSavedCardModel[];
  cardsByTags: (SeaSavedCardModel | AirSavedCardModel)[];
  closeUpdateAllCardsModal: () => void;
  updateFilteredCards: (response: AirSavedCardModel) => {
    type: string;
    payload: AirSavedCardModel;
  };
};

type UseAirUpdateAllCardsActionReturnType = {
  updateAllAirCards: () => void;
  canCloseUpdateAllAirCardsModal: boolean;
  airProgress: {
    total: number;
    current: number;
  };
  airResults: { reference: string | null; isSuccess: boolean }[];
  airResetState: () => void;
};

export const useAirUpdateAllCardsAction = ({
  filteredCards,
  airSavedCards,
  cardsByTags,
  updateFilteredCards,
}: UseAirUpdateAllCardsActionParams): UseAirUpdateAllCardsActionReturnType => {
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState<UseAirUpdateAllCardsActionReturnType["airProgress"]>({
    total: 0,
    current: 0,
  });
  const [results, setResults] = useState<{ reference: string | null; isSuccess: boolean }[]>([]);
  const { isCurrentViewFilteredCards, isCurrentViewMain, isCurrentViewCardsByTag } = usePanelViews();
  const { updateAllCards } = createAirUpdateCardHook({
    updateFilteredCards,
  })();

  const resetState = () => {
    setResults([]);
    setProgress({ total: 0, current: 0 });
    setIsLoading(false);
  };

  const updateCards = async (cards: AirSavedCardModel[]) => {
    const cardGroups = cards.reduce<AirSavedCardModel[][]>((groups, card, index) => {
      const groupIndex = Math.floor(index / 5);
      if (!groups[groupIndex]) {
        groups[groupIndex] = [];
      }
      groups[groupIndex].push(card);
      return groups;
    }, []);

    setIsLoading(true);
    setProgress({ total: cards.length, current: 0 });
    for (const group of cardGroups) {
      const { results, canUpdateMore } = await updateAllCards(group);
      setResults((prev) => [...prev, ...results]);
      setProgress((prev) => ({ ...prev, current: prev.current + group.length }));
      if (!canUpdateMore) {
        break;
      }
    }
    setIsLoading(false);
  };

  const updateAllSeaCards = async () => {
    try {
      if (isCurrentViewFilteredCards) {
        await updateCards(filteredCards.filter((card) => isAirSavedCardModel(card)));
      }
      if (isCurrentViewMain) {
        await updateCards(airSavedCards);
      }
      if (isCurrentViewCardsByTag) {
        await updateCards(cardsByTags.filter((card) => isAirSavedCardModel(card)));
      }
    } catch (_) {}
  };

  return {
    updateAllAirCards: updateAllSeaCards,
    canCloseUpdateAllAirCardsModal: !isLoading,
    airProgress: progress,
    airResults: results,
    airResetState: resetState,
  };
};
